import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  CreateTaskTypeDef,
  StatusConfig,
  StatusCustomMapping,
  UpdateTaskTypeDef,
} from '@scrambl/shared'

import type { ProjectTypeStatusSource } from '@/lib/api/task-status.api'
import { taskStatusApi } from '@/lib/api/task-status.api'
import { taskTypesApi } from '@/lib/api/task-types.api'
import { invalidateProjectStatusCaches } from '@/lib/project-status-invalidation'
import { queryKeys } from './query-keys'

export function useTaskTypes(includeArchived = false, enabled = true) {
  return useQuery({
    queryKey: queryKeys.taskTypes.list(includeArchived),
    queryFn: () => taskTypesApi.list(includeArchived),
    enabled,
    staleTime: 60_000,
  })
}

/** A project's enabled task-type subset (empty subset falls back to all org types server-side). */
export function useProjectTaskTypes(
  projectId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.taskTypes.byProject(projectId ?? 'none'),
    queryFn: () => taskTypesApi.listForProject(projectId as string),
    enabled: Boolean(projectId) && enabled,
    staleTime: 60_000,
  })
}

/**
 * The task types to show for a task's context: the project subset when a
 * projectId is given, otherwise the full org catalog (my-tasks / cross-project).
 */
export function useTaskTypesForContext(
  projectId: string | null | undefined,
  enabled = true,
) {
  const hasProject = Boolean(projectId)
  const org = useTaskTypes(false, enabled && !hasProject)
  const project = useProjectTaskTypes(projectId, enabled && hasProject)
  return projectId ? project : org
}

/** Projects a task type is tied to. Lazy — gate `enabled` on the row being expanded. */
export function useTaskTypeProjects(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: id
      ? queryKeys.taskTypes.projects(id)
      : ['task-types', 'projects', 'none'],
    queryFn: () => taskTypesApi.listProjectsUsingType(id as string),
    enabled: Boolean(id) && enabled,
    staleTime: 60_000,
  })
}

export function useTaskTypeTemplates() {
  return useQuery({
    queryKey: queryKeys.taskTypes.templates(),
    queryFn: () => taskTypesApi.listTemplates(),
    staleTime: 5 * 60_000,
  })
}

export function useTaskTypeStatuses(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? queryKeys.taskTypes.statuses(id)
      : ['task-types', 'statuses', 'none'],
    queryFn: () => taskTypesApi.getStatuses(id as string),
    enabled: Boolean(id),
    staleTime: 60_000,
  })
}

function useInvalidateTaskTypes() {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.taskTypes.all })
}

export function useCreateTaskType() {
  const invalidate = useInvalidateTaskTypes()
  return useMutation({
    mutationFn: (dto: CreateTaskTypeDef) => taskTypesApi.create(dto),
    onSuccess: () => {
      toast.success('Task type created')
      invalidate()
    },
    onError: (e: Error) =>
      toast.error(e.message || 'Failed to create task type'),
  })
}

export function useInstallTaskType() {
  const invalidate = useInvalidateTaskTypes()
  return useMutation({
    mutationFn: (templateKey: string) => taskTypesApi.install(templateKey),
    onSuccess: t => {
      toast.success(`Added ${t.name}`)
      invalidate()
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to add template'),
  })
}

export function useUpdateTaskType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTaskTypeDef }) =>
      taskTypesApi.update(id, dto),
    onSuccess: () => {
      toast.success('Task type updated')
      queryClient.invalidateQueries({ queryKey: queryKeys.taskTypes.all })
      // Name/icon/color live on the type but are denormalized into each task's
      // taskTypeDef summary — refresh task views so chips reflect the change.
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all })
    },
    onError: (e: Error) =>
      toast.error(e.message || 'Failed to update task type'),
  })
}

export function useSetTaskTypeEnabled() {
  const invalidate = useInvalidateTaskTypes()
  return useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      taskTypesApi.setEnabled(id, isEnabled),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message || 'Failed to toggle task type'),
  })
}

export function useSetTaskTypeArchived() {
  const invalidate = useInvalidateTaskTypes()
  return useMutation({
    mutationFn: ({ id, isArchived }: { id: string; isArchived: boolean }) =>
      taskTypesApi.setArchived(id, isArchived),
    onSuccess: (_d, v) => {
      toast.success(v.isArchived ? 'Task type archived' : 'Task type restored')
      invalidate()
    },
    onError: (e: Error) =>
      toast.error(e.message || 'Failed to archive task type'),
  })
}

export function useReorderTaskTypes() {
  const invalidate = useInvalidateTaskTypes()
  return useMutation({
    mutationFn: (ids: string[]) => taskTypesApi.reorder(ids),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message || 'Failed to reorder'),
  })
}

export function useDeleteTaskType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reassignToId }: { id: string; reassignToId?: string }) =>
      taskTypesApi.remove(id, reassignToId),
    onSuccess: res => {
      toast.success(
        res.reassignedCount > 0
          ? `Task type deleted, ${res.reassignedCount} task(s) reassigned`
          : 'Task type deleted',
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.taskTypes.all })
      // Reassignment changed task records — refresh task views too.
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all })
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to delete task type'),
  })
}

export function useAddProjectTaskType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      projectId,
      taskTypeDefId,
    }: {
      projectId: string
      taskTypeDefId: string
    }) => taskTypesApi.addToProject(projectId, taskTypeDefId),
    onSuccess: (_d, v) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.taskTypes.byProject(v.projectId),
      }),
    onError: (e: Error) => toast.error(e.message || 'Failed to add task type'),
  })
}

export function useRemoveProjectTaskType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      projectId,
      taskTypeDefId,
    }: {
      projectId: string
      taskTypeDefId: string
    }) => taskTypesApi.removeFromProject(projectId, taskTypeDefId),
    onSuccess: (_d, v) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.taskTypes.byProject(v.projectId),
      }),
    onError: (e: Error) =>
      toast.error(e.message || 'Failed to remove task type'),
  })
}

export function useAddTaskTypeToAllProjects() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => taskTypesApi.addToAllProjects(id),
    onSuccess: () => {
      toast.success('Added to all projects')
      // Touches every project's subset.
      queryClient.invalidateQueries({ queryKey: queryKeys.taskTypes.all })
    },
    onError: (e: Error) =>
      toast.error(e.message || 'Failed to add to all projects'),
  })
}

export function useSetTaskTypeStatuses() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      statuses,
      options,
    }: {
      id: string
      statuses: StatusConfig[]
      options?: {
        migrateExistingTasks?: boolean
        customMappings?: StatusCustomMapping[]
      }
    }) => taskTypesApi.setStatuses(id, statuses, options),
    onSuccess: (_d, v) => {
      toast.success('Org default statuses updated')
      queryClient.invalidateQueries({ queryKey: queryKeys.taskTypes.all })
      queryClient.invalidateQueries({
        queryKey: queryKeys.taskTypes.statuses(v.id),
      })
      // Org defaults apply live in inheriting projects and the save may have
      // remapped their tasks — refresh task views everywhere.
      queryClient.invalidateQueries({ queryKey: ['tasks'], refetchType: 'all' })
    },
    onError: (e: Error) =>
      toast.error(e.message || 'Failed to update org default statuses'),
  })
}

/** A task type's status configuration within a project (source + status sets). */
export function useProjectTaskTypeStatuses(
  organizationId: string | undefined,
  projectId: string,
  taskTypeDefId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.taskTypes.projectTypeStatuses(projectId, taskTypeDefId),
    queryFn: () =>
      taskStatusApi.getProjectTypeStatuses(
        organizationId as string,
        projectId,
        taskTypeDefId,
      ),
    enabled: Boolean(organizationId) && enabled,
    staleTime: 60_000,
  })
}

/**
 * Every status the project can show across ALL its task types (union of each
 * type's resolved set + base project chain, deduped by id). Backs the Tasks
 * page Status filter so it stays complete regardless of which task types are
 * loaded on screen.
 */
export function useProjectAllTypeStatuses(
  organizationId: string | undefined,
  projectId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.taskTypes.projectAllTypeStatuses(projectId as string),
    queryFn: () =>
      taskStatusApi.getProjectAllTypeStatuses(
        organizationId as string,
        projectId as string,
      ),
    enabled: Boolean(organizationId) && Boolean(projectId) && enabled,
    staleTime: 60_000,
  })
}

export function useSetProjectTaskTypeStatuses() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      organizationId,
      projectId,
      taskTypeDefId,
      body,
    }: {
      organizationId: string
      projectId: string
      taskTypeDefId: string
      body: {
        statusSource: ProjectTypeStatusSource
        statuses?: StatusConfig[]
        migrateExistingTasks?: boolean
        customMappings?: StatusCustomMapping[]
      }
    }) =>
      taskStatusApi.setProjectTypeStatusSource(
        organizationId,
        projectId,
        taskTypeDefId,
        body,
      ),
    onSuccess: async (_d, v) => {
      await invalidateProjectStatusCaches(queryClient, v.projectId)
    },
    onError: (e: Error) =>
      toast.error(e.message || 'Failed to update task type statuses'),
  })
}
